import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { adTagUrl, playPreroll } from '@/lib/ads/ima';

interface Props {
  /** When true, play a pre-roll then call onComplete. When false, onComplete immediately. */
  active: boolean;
  onComplete: () => void;
}

/**
 * Pre-roll ad overlay for Limited users who've spent their daily free plays.
 * Self-contained: runs a single VAST ad via the IMA SDK, then calls onComplete
 * to release the content player. NEVER blocks — if ads are disabled, the SDK
 * fails, or the VAST is empty/errored, it completes immediately (see ima.ts).
 */
export function Preroll({ active, onComplete }: Props): JSX.Element | null {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let cancelled = false;
    const finish = (): void => {
      if (!cancelled) onComplete();
    };

    if (!active) return;
    const tag = adTagUrl();
    // Refs are present because we render the container whenever `active`.
    if (!tag || !containerRef.current || !videoRef.current) {
      finish();
      return;
    }
    void playPreroll({
      adTagUrl: tag,
      adContainer: containerRef.current,
      contentVideo: videoRef.current,
    }).then(finish);

    return () => {
      cancelled = true;
    };
    // onComplete is stable from the caller (useCallback); active drives reruns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!active) return null;

  return (
    <div className="preroll" data-testid="preroll-overlay" role="presentation">
      <div className="preroll__ad" ref={containerRef} />
      {/* IMA needs a content video handle; hidden — the real player mounts after. */}
      <video ref={videoRef} muted playsInline style={{ display: 'none' }} />
      <p className="preroll__label muted">{t('ads.preroll')}</p>
    </div>
  );
}
